import type { TVesselJourneyResponse } from '@/api/v1/resources/vesselJourney'

export type TOption = {
  label: string
  value: string
}

export function formattedMultiOptions(value?: string[]): TOption[] {
  return value ? value.map((a) => ({ label: a, value: a })) : []
}

export type TSortVesselsByArrivalPriorityOptions = {
  /** Port UNLOCODE to prioritize (e.g., 'BRSSZ' for Santos) */
  priorityPortUnlocode: string
  /** If true, sorts oldest dates first; otherwise sorts recent dates first (default: false) */
  sortOldestFirst?: boolean
}

/**
 * Sorts vessels by arrival priority.
 *
 * Sorting priorities:
 * 1. Vessels with arrival time data (actualTime or estimatedTime) come first
 * 2. Among vessels with arrival times, prioritize those arriving at the specified port
 * 3. Within same priority groups, sort by time (direction controlled by sortOldestFirst)
 * 4. For vessels without arrival times, use ship.eta as fallback
 *
 * @param vessels - Array of vessel journey responses to sort
 * @param options - Sorting options including priority port and sort direction
 * @returns A new sorted array (does not mutate the input)
 */
export function sortVesselsByArrivalPriority(
  vessels: TVesselJourneyResponse[],
  options: TSortVesselsByArrivalPriorityOptions
): TVesselJourneyResponse[] {
  const { priorityPortUnlocode, sortOldestFirst = false } = options

  return [...vessels].sort((a: TVesselJourneyResponse, b: TVesselJourneyResponse) => {
    // Check if vessel is arriving at the priority port
    const aArrivingAtPriorityPort = a.arrival?.port?.unlocode === priorityPortUnlocode
    const bArrivingAtPriorityPort = b.arrival?.port?.unlocode === priorityPortUnlocode

    // Check if vessels have meaningful arrival time data
    const aHasArrivalTime = !!(a.arrival?.actualTime || a.arrival?.estimatedTime)
    const bHasArrivalTime = !!(b.arrival?.actualTime || b.arrival?.estimatedTime)

    // Priority 1: Vessels with arrival time data (regardless of port) - these should be first
    if (aHasArrivalTime && !bHasArrivalTime) return -1
    if (!aHasArrivalTime && bHasArrivalTime) return 1

    // Priority 2: Among vessels with arrival times, prioritize the specified port
    if (aHasArrivalTime && bHasArrivalTime) {
      if (aArrivingAtPriorityPort && !bArrivingAtPriorityPort) return -1
      if (!aArrivingAtPriorityPort && bArrivingAtPriorityPort) return 1

      // Both have arrival times and same port priority, sort by time
      const getArrivalTime = (vessel: TVesselJourneyResponse) => {
        if (vessel.arrival?.actualTime) return new Date(vessel.arrival.actualTime).getTime()
        if (vessel.arrival?.estimatedTime) return new Date(vessel.arrival.estimatedTime).getTime()
        return Infinity
      }
      const timeA = getArrivalTime(a)
      const timeB = getArrivalTime(b)
      return sortOldestFirst ? timeA - timeB : timeB - timeA
    }

    // Priority 3: Among vessels without arrival times, prioritize the specified port
    if (!aHasArrivalTime && !bHasArrivalTime) {
      if (aArrivingAtPriorityPort && !bArrivingAtPriorityPort) return -1
      if (!aArrivingAtPriorityPort && bArrivingAtPriorityPort) return 1

      // Both no arrival times and same port priority, sort by ship.eta as fallback
      const aEta = a.ship.eta ? new Date(a.ship.eta).getTime() : Infinity
      const bEta = b.ship.eta ? new Date(b.ship.eta).getTime() : Infinity
      return sortOldestFirst ? aEta - bEta : bEta - aEta
    }

    return 0
  })
}
